import { Badge, Flex, Text, Tooltip } from "@radix-ui/themes";
import { useTranslation } from "react-i18next";

import {
  CONNECTOR_CUSTOM_LOGOS,
  CONNECTOR_PHOSPHOR_ICONS,
  CONNECTOR_PHOSPHOR_WEIGHT,
  connectorIconSource,
} from "../connectorLogos";
import { type UsedConnector } from "../utils/usedConnectors";

export type UsedConnectorsChipsProps = {
  connectors: readonly UsedConnector[];
  /** Section heading. Defaults to "Connectors used". */
  label?: string;
  /**
   * Prefix for testids so chat / project / review surfaces stay unique.
   * Produces `{prefix}-used-connectors-section` and chip/tooltip/icon ids.
   */
  testIdPrefix?: string;
};

function scopedTestId(prefix: string | undefined, base: string): string {
  return prefix ? `${prefix}-${base}` : base;
}

/**
 * Compact deduped connector pills with hover/focus tooltips listing which tools ran.
 * Returns null when nothing was used (no empty placeholder).
 * Uses Radix Themes Badge + Tooltip (same pattern as VersionApprovalBadge),
 * with a focusable button trigger so keyboard users can open the tool list.
 */
export function UsedConnectorsChips({
  connectors,
  label = "Connectors used",
  testIdPrefix,
}: UsedConnectorsChipsProps) {
  const { t } = useTranslation("ui");
  if (connectors.length === 0) return null;

  const sectionTestId = scopedTestId(testIdPrefix, "used-connectors-section");

  return (
    <Flex
      direction="column"
      gap="2"
      data-testid={sectionTestId}
      aria-label={t("askPuntt.usedConnectorsChips.connectorsUsed")}
    >
      <Text
        as="p"
        size="2"
        weight="bold"
        className="text-puntt-neutral-gray-12"
      >
        {label}
      </Text>
      <Flex wrap="wrap" gap="1">
        {connectors.map((connector) => (
          <ConnectorChip
            key={connector.connectorId}
            connector={connector}
            testIdPrefix={testIdPrefix}
          />
        ))}
      </Flex>
    </Flex>
  );
}

function ConnectorChip({
  connector,
  testIdPrefix,
}: {
  connector: UsedConnector;
  testIdPrefix?: string;
}) {
  const timesLabel =
    connector.callCount === 1
      ? "used 1 time"
      : `used ${connector.callCount} times`;
  const toolSummary = connector.tools
    .map((tool) =>
      tool.count > 1 ? `${tool.displayName} × ${tool.count}` : tool.displayName,
    )
    .join(", ");
  const ariaLabel = toolSummary
    ? `${connector.name} — ${timesLabel}. Tools: ${toolSummary}`
    : `${connector.name} — ${timesLabel}`;

  const tooltipTestId = scopedTestId(
    testIdPrefix,
    `used-connector-tooltip-${connector.connectorId}`,
  );
  const chipTestId = scopedTestId(
    testIdPrefix,
    `used-connector-chip-${connector.connectorId}`,
  );
  const iconTestId = scopedTestId(
    testIdPrefix,
    `used-connector-icon-${connector.connectorId}`,
  );

  const tooltipBody = (
    <Flex
      direction="column"
      gap="1"
      className="max-w-xs text-left"
      data-testid={tooltipTestId}
    >
      <Text size="1" weight="bold">
        {connector.name}: {timesLabel}
      </Text>
      <ul className="m-0 list-disc pl-4 text-[11px] leading-snug">
        {connector.tools.map((tool) => (
          <li key={tool.displayName}>
            {tool.displayName}
            {tool.count > 1 ? ` × ${tool.count}` : ""}
          </li>
        ))}
      </ul>
    </Flex>
  );

  const PhosphorIcon = CONNECTOR_PHOSPHOR_ICONS[connector.connectorId];
  const customLogoSrc = CONNECTOR_CUSTOM_LOGOS[connector.connectorId];
  const iconSource = connectorIconSource(connector.connectorId);

  return (
    <Tooltip content={tooltipBody}>
      <button
        type="button"
        data-testid={chipTestId}
        aria-label={ariaLabel}
        className="m-0 inline-flex max-w-full cursor-default border-0 bg-transparent p-0"
      >
        <Badge
          size="1"
          variant="soft"
          color="gray"
          radius="full"
          className="max-w-full gap-1"
          aria-hidden="true"
        >
          <span
            aria-hidden="true"
            className="inline-flex size-3.5 shrink-0 items-center justify-center text-[13px] leading-none"
            data-testid={iconTestId}
            data-icon-source={iconSource}
          >
            {PhosphorIcon ? (
              <PhosphorIcon size={14} weight={CONNECTOR_PHOSPHOR_WEIGHT} />
            ) : customLogoSrc ? (
              <img
                src={customLogoSrc}
                alt=""
                width={14}
                height={14}
                className="size-3.5 object-contain"
                draggable={false}
              />
            ) : (
              connector.icon
            )}
          </span>
          <span className="min-w-0 truncate">{connector.name}</span>
        </Badge>
      </button>
    </Tooltip>
  );
}
